import * as React from 'react';
import { useState } from 'react';

import { isAuthenticatedUser, get as userGet } from 'js/lib/user';

import type { SurveyFieldConfig } from 'bundles/expression-of-interest/components/ddp-multistep/SurveyQuestionField';
import SurveySectionForm from 'bundles/expression-of-interest/components/ddp-multistep/SurveySectionForm';
import SurveyTerminalScreen from 'bundles/expression-of-interest/components/ddp-multistep/SurveyTerminalScreen';
import { getSplitName } from 'bundles/expression-of-interest/utils/util';
import countries from 'bundles/page/common/countries';

import _t from 'i18n!nls/expression-of-interest';

type Props = {
  programName?: string;
};

const TOTAL_STEPS = 2;

// Prefills from the client-side user when logged in.
const getPrefilledAnswers = (): Record<string, string> => {
  if (!isAuthenticatedUser()) {
    return {};
  }

  const currentUser = userGet();
  const { firstName, lastName } = getSplitName(currentUser.fullName);

  return {
    ...(firstName ? { firstName } : {}),
    ...(lastName ? { lastName } : {}),
    ...(currentUser.email_address ? { emailAddress: currentUser.email_address } : {}),
  };
};

// TODO: Placeholder options until server-defined `Question` definitions are wired in.
const getEducationLevelOptions = () => [
  { id: 'high_school', text: _t('High school') },
  { id: 'bachelors', text: _t("Bachelor's degree") },
  { id: 'masters', text: _t("Master's degree") },
  { id: 'doctorate', text: _t('Doctorate') },
  { id: 'other', text: _t('Other') },
];

const getStartDateOptions = () => [
  { id: 'within_3_months', text: _t('Within 3 months') },
  { id: '3_to_6_months', text: _t('3-6 months') },
  { id: '6_to_12_months', text: _t('6-12 months') },
  { id: 'just_researching', text: _t('Just researching') },
];

const MultiStepEoiSurvey = ({ programName }: Props) => {
  const [stepIndex, setStepIndex] = useState<0 | 1 | 2>(0);
  const [answers, setAnswers] = useState<Record<string, string>>(getPrefilledAnswers);

  const heading = _t("Get the program details to see if it's right for you");

  const personalInfoFields: SurveyFieldConfig[] = [
    { id: 'firstName', kind: 'text', label: _t('First name'), placeholder: _t('First name') },
    { id: 'lastName', kind: 'text', label: _t('Last name'), placeholder: _t('Last name') },
    { id: 'emailAddress', kind: 'email', label: _t('Email address'), placeholder: _t('Email address') },
    { id: 'phoneNumber', kind: 'tel', label: _t('Phone number'), placeholder: _t('Phone number') },
  ];

  const educationAndTimelineFields: SurveyFieldConfig[] = [
    {
      id: 'country',
      kind: 'dropdown',
      label: _t('Country of residence'),
      placeholder: _t('Country of residence'),
      options: countries.map((country) => ({ id: country.code, text: country.name })),
    },
    {
      id: 'highestLevelOfEducation',
      kind: 'dropdown',
      label: _t('Education level'),
      placeholder: _t('Education level'),
      options: getEducationLevelOptions(),
    },
    {
      id: 'intendedStartDate',
      kind: 'dropdown',
      label: _t('When do you want to start?'),
      placeholder: _t('When do you want to start?'),
      fullWidth: true,
      options: getStartDateOptions(),
    },
  ];

  const handleFieldChange = (fieldId: string, value: string) => {
    setAnswers((prevAnswers) => ({ ...prevAnswers, [fieldId]: value }));
  };

  if (stepIndex === 0) {
    return (
      <SurveySectionForm
        key="personal-info"
        heading={heading}
        submitLabel={_t('Continue')}
        fields={personalInfoFields}
        values={answers}
        onFieldChange={handleFieldChange}
        onSubmit={() => setStepIndex(1)}
        stepNumber={1}
        totalSteps={TOTAL_STEPS}
      />
    );
  }

  if (stepIndex === 1) {
    return (
      <SurveySectionForm
        key="education-and-timeline"
        heading={heading}
        submitLabel={_t('Send me more info')}
        fields={educationAndTimelineFields}
        values={answers}
        onFieldChange={handleFieldChange}
        onSubmit={() => setStepIndex(2)}
        stepNumber={2}
        totalSteps={TOTAL_STEPS}
        focusHeadingOnMount
        fieldsOptional
      />
    );
  }

  return <SurveyTerminalScreen firstName={answers.firstName ?? ''} programName={programName} />;
};

export default MultiStepEoiSurvey;
